/*
 * Super Admin → Demo Environments.
 * Manage the three persistent demos (School / Authorizer / Sales): status + data
 * size, reveal/copy reusable credentials, one-click Launch (clean per-tab
 * impersonation), Reset-to-baseline ("RESET DEMO"), and a printable cheat sheet.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Batch1ShowcaseCard } from "./Batch1ShowcaseCard";
import { Loader2, Rocket, RotateCcw, Copy, Eye, Printer, GraduationCap, Building2, BadgeDollarSign, KeyRound, CalendarClock } from "lucide-react";
import { HopeRenameCard } from "./HopeRenameCard";

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { school: GraduationCap, authorizer: Building2, sales: BadgeDollarSign };

export default function DemoManagerPage({ session }) {
  const [envs, setEnvs] = useState(null);
  const [creds, setCreds] = useState({});
  const [resetTarget, setResetTarget] = useState(null);
  const [resetText, setResetText] = useState("");
  const [busy, setBusy] = useState("");
  const headers = { "x-user-role": session?.user?.role || "admin", "x-user-id": session?.user?.id || "" };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/demo-manager/environments`, { headers });
      setEnvs(r.data.environments || []);
    } catch { toast.error("Failed to load demo environments"); }
  }, [headers]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reveal = async (env) => {
    try {
      const r = await axios.get(`${API}/api/admin/demo-manager/${env}/credentials`, { headers });
      setCreds((p) => ({ ...p, [env]: r.data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "No credentials"); }
  };
  const copy = (env) => {
    const c = creds[env];
    if (!c) return;
    navigator.clipboard.writeText(`Login: ${c.login_email}\nPassword: ${c.password}\nURL: ${c.login_url}`);
    toast.success("Credentials copied");
  };

  const launch = async (env) => {
    setBusy(`launch-${env}`);
    try {
      const { data } = await axios.post(`${API}/api/admin/demo-manager/${env}/launch`, {}, { headers });
      const payload = { token: data.session_token, realm: data.realm, env, redirect: data.realm === "tenant" ? "/diagnostic" : data.redirect,
        email: data.user?.email, name: data.user?.name || data.user?.full_name };
      if (data.realm === "tenant") payload.diag_session = { user: data.user, tenant: data.tenant, session_token: data.session_token, loginAt: new Date().toISOString() };
      const hash = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
      window.open(`/demo-launch#${hash}`, "_blank", "noopener");
      toast.success("Demo launched in a new tab");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Launch failed"); } finally { setBusy(""); }
  };

  const doReset = async () => {
    setBusy("reset");
    try {
      await axios.post(`${API}/api/admin/demo-manager/${resetTarget}/reset`, { confirm: resetText }, { headers });
      toast.success("Demo reset to baseline");
      setResetTarget(null); setResetText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); } finally { setBusy(""); }
  };

  const [refresh, setRefresh] = useState(null);
  const previewRefresh = async () => {
    setBusy("refresh-preview");
    try {
      const { data } = await axios.post(`${API}/api/admin/demo-manager/sales/refresh-dates?dry_run=true`, {}, { headers });
      setRefresh(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not preview the date refresh"); } finally { setBusy(""); }
  };
  const applyRefresh = async () => {
    setBusy("refresh-apply");
    try {
      const { data } = await axios.post(`${API}/api/admin/demo-manager/sales/refresh-dates?dry_run=false`, {}, { headers });
      toast.success(`Sales demo dates moved to ${data.reset_date}`);
      setRefresh(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Date refresh failed"); } finally { setBusy(""); }
  };

  const printSheet = () => window.print();

  if (!envs) return <div className="py-16 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8" data-testid="demo-manager-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#0b1220]"><Rocket className="h-6 w-6 text-[#007a9e]" /><h1 className="text-2xl font-bold">Demo Environments</h1></div>
          <p className="mt-1 text-sm text-[#6b7280]">Launch, inspect and reset the persistent demos. All demo data is isolated from production metrics.</p>
        </div>
        <Button variant="outline" onClick={printSheet} data-testid="demo-print-sheet"><Printer className="h-4 w-4 mr-1.5" />Print cheat sheet</Button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {envs.map((e) => {
          const Icon = ICONS[e.env] || Rocket;
          const c = creds[e.env];
          return (
            <div key={e.env} className="rounded-xl border border-[#e5e7eb] bg-white p-5 flex flex-col" data-testid={`demo-card-${e.env}`}>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-lg bg-[#0b1220]/5 flex items-center justify-center text-[#0b1220]"><Icon className="h-5 w-5" /></div>
                <div><div className="font-bold text-[#111827]">{e.name}</div><div className="text-[11.5px] text-[#6b7280]">{e.account_type}</div></div>
              </div>
              <p className="mt-3 text-[13px] text-[#4b5563]">{e.purpose}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(e.capabilities || []).map((cap) => <span key={cap} className="text-[11.5px] rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[#374151]">{cap}</span>)}
              </div>
              <div className="mt-3 rounded-lg bg-[#f9fafb] border border-[#eef0f2] p-2.5 text-[12px] text-[#374151]" data-testid={`demo-size-${e.env}`}>
                {Object.entries(e.data_size || {}).map(([k, v]) => <span key={k} className="inline-block mr-3"><b>{v}</b> {k.replace(/_/g, " ")}</span>)}
              </div>
              {e.env === "sales" && e.seed_reset_date && (
                <div className="mt-2 text-[12px] text-[#6b7280]" data-testid="demo-sales-dates-as-of">Demo dates as of <b className="text-[#111827]">{e.seed_reset_date}</b></div>
              )}
              <div className="mt-3 text-[12px]">
                <div className="text-[#6b7280]">Login</div>
                <div className="font-mono text-[#111827] truncate" data-testid={`demo-email-${e.env}`}>{e.login_email || "—"}</div>
                {c && <div className="mt-1 font-mono text-[#111827]" data-testid={`demo-password-${e.env}`}>Password: {c.password}</div>}
              </div>
              <div className="mt-auto pt-4 flex flex-wrap gap-2">
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" disabled={busy === `launch-${e.env}` || !e.account_ready} onClick={() => launch(e.env)} data-testid={`demo-launch-${e.env}`}>
                  {busy === `launch-${e.env}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}<span className="ml-1.5">Launch</span>
                </Button>
                {c
                  ? <Button size="sm" variant="outline" onClick={() => copy(e.env)} data-testid={`demo-copy-${e.env}`}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
                  : <Button size="sm" variant="outline" onClick={() => reveal(e.env)} data-testid={`demo-reveal-${e.env}`}><Eye className="h-3.5 w-3.5 mr-1" />Reveal</Button>}
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setResetTarget(e.env); setResetText(""); }} data-testid={`demo-reset-${e.env}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset</Button>
                {e.env === "sales" && (
                  <Button size="sm" variant="outline" disabled={busy === "refresh-preview"} onClick={previewRefresh} data-testid="demo-refresh-dates-sales">
                    {busy === "refresh-preview" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5 mr-1" />}Refresh dates
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Batch1ShowcaseCard headers={headers} onDone={load} />

      {/* Printable Demo Account Reference cheat sheet */}
      <div className="mt-8 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="demo-cheat-sheet">
        <div className="flex items-center gap-2 mb-3"><KeyRound className="h-4 w-4 text-[#007a9e]" /><h2 className="text-base font-bold text-[#111827]">Demo Account Reference</h2></div>
        <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full min-w-[520px] text-[12px]">
          <thead className="text-[11.5px] uppercase text-[#6b7280]"><tr><th className="text-left py-1">Environment</th><th className="text-left">Role</th><th className="text-left">Login</th><th className="text-left">Password</th><th className="text-left">URL</th></tr></thead>
          <tbody>
            {envs.map((e) => (
              <tr key={e.env} className="border-t border-[#f0f1f3]">
                <td className="py-1.5">{e.name}</td><td>{e.account_type}</td>
                <td className="font-mono">{e.login_email || "—"}</td>
                <td className="font-mono">{creds[e.env]?.password || <button className="text-[#2563eb] underline" onClick={() => reveal(e.env)}>reveal</button>}</td>
                <td className="font-mono">/diagnostic/login</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <HopeRenameCard headers={headers} />

<RefreshDatesDialog report={refresh} busy={busy === "refresh-apply"} onClose={() => setRefresh(null)} onApply={applyRefresh} />

            <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="demo-reset-modal">
          <DialogHeader>
            <DialogTitle>Reset demo to baseline</DialogTitle>
            <DialogDescription>This restores the "{resetTarget}" demo to its seeded baseline. It only affects demo data — never production.</DialogDescription>
          </DialogHeader>
          <p className="text-[13px] text-[#374151]">Type <b>RESET DEMO</b> to confirm.</p>
          <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET DEMO" data-testid="demo-reset-confirm-input" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy === "reset" || resetText.trim().toUpperCase() !== "RESET DEMO"} onClick={doReset} data-testid="demo-reset-confirm">
              {busy === "reset" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}Reset demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const RefreshDatesDialog = ({ report, busy, onClose, onApply }) => {
  const rows = Object.entries(report || {}).filter(([, v]) => v && typeof v === "object" && "update" in v);
  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="demo-refresh-modal">
        <DialogHeader>
          <DialogTitle>Move Sales demo dates to today</DialogTitle>
          <DialogDescription>Same 48 leads and records, re-dated to {report?.reset_date} so this month's commission tiles stay filled. Demo data only.</DialogDescription>
        </DialogHeader>
        <table className="w-full text-[12.5px]" data-testid="demo-refresh-preview">
          <thead className="text-[11.5px] uppercase text-[#6b7280]"><tr><th className="text-left py-1">Records</th><th className="text-right">Update</th><th className="text-right">Insert</th><th className="text-right">Delete</th></tr></thead>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-t border-[#f0f1f3]"><td className="py-1">{k.replace("sales_demo_records.", "").replace(/_/g, " ")}</td><td className="text-right">{v.update}</td><td className="text-right">{v.insert}</td><td className="text-right">{v.delete}</td></tr>
            ))}
          </tbody>
        </table>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#007a9e] hover:bg-[#00607d] text-white" disabled={busy} onClick={onApply} data-testid="demo-refresh-apply">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}Apply new dates
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
